import React, {useState, useEffect} from "react";
import {PiMagicWand} from "react-icons/pi";
import {transcribe} from "../api/transcribe/transcribe";
import {Loader2} from "lucide-react";
import {useTranslation} from "react-i18next";
import Mixpanel from "../utils/mixpanel";
import {Button} from "./Button";

interface CustomButtonProps {
    onTranscriptionComplete: (result: string, audioUuid: string) => void;
    disabled?: boolean;
    audioFile: File | null;
}

export function TranscriptionButton({
    onTranscriptionComplete,
    disabled = false,
    audioFile,
}: CustomButtonProps) {
    const [isProcessing, setIsProcessing] = useState(false);
    const [progress, setProgress] = useState(0);
    const [message, setMessage] = useState("");
    const {t} = useTranslation("global");

    const handleTranscribe = async () => {
        Mixpanel.track("Transcription started");
        if (!(audioFile instanceof File)) {
            console.error("Invalid audio file");
            Mixpanel.track("Invalid audio file", {
                error: "Invalid audio file",
            });
            return;
        }

        setIsProcessing(true);
        setProgress(0);
        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 500000);
            await transcribe(audioFile, {
                onProgress: (progress, message) => {
                    console.log("progress", progress);
                    setProgress(progress);
                },
                onComplete: (result) => {
                    setIsProcessing(false);
                    onTranscriptionComplete(
                        result["transcription"] as string,
                        result["audio_uuid"] as string
                    );
                },
                onError: (error) => {
                    console.error("Transcription error:", error);
                    Mixpanel.track("Transcription error", {
                        error: error,
                    });
                },
            });
            clearTimeout(timeoutId);
        } catch (error: any) {
            console.error("Transcription failed:", error);
            if (error.name === "AbortError") {
                console.error("Request timed out after 5 minutes");
            }
            Mixpanel.track("Transcription failed", {
                error: error.message,
            });
        } finally {
            setIsProcessing(false);
            setProgress(0);
            Mixpanel.track("Transcription done", {
                error: "Transcription done",
            });
        }
    };

    useEffect(() => {
        console.log("progress", progress);
        if (progress === 0) {
            setMessage(t("transcription.progress_message_0"));
        } else if (progress === 10) {
            setMessage(t("transcription.progress_message_1"));
        } else if (progress === 20) {
            setMessage(t("transcription.progress_message_2"));
        } else if (progress === 30) {
            setMessage(t("transcription.progress_message_3"));
        } else if (progress === 60) {
            setMessage(t("transcription.progress_message_4"));
        } else if (progress === 95) {
            setMessage(t("transcription.progress_message_5"));
        }
    }, [progress]);

    return (
        <Button
            onClick={handleTranscribe}
            disabled={disabled || isProcessing}
            className="w-full"
            text={isProcessing ? message : t("transcription.button")}
            key={message} // Add key to force re-render when message changes
            icon={
                isProcessing ? (
                    <Loader2 className="h-[22px] w-[22px] animate-spin " />
                ) : (
                    <PiMagicWand className="h-[22px] w-[22px]" />
                )
            }
        />
    );
}
